<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import siteVisitsAPI from 'dashboard/api/nexceo/siteVisits';

const props = defineProps({
  visit: { type: Object, default: null },
  isOpen: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'updated']);

const store = useStore();
const { t } = useI18n();

const isActing = ref(false);
const isRetryingMeet = ref(false);
const retryMeetResult = ref(null);
const showMeetLinkInput = ref(false);
const meetLinkInput = ref('');
const isSavingMeetLink = ref(false);
const actionError = ref('');
const showCancel = ref(false);
const cancelReason = ref('');
const showReschedule = ref(false);
const rescheduleAt = ref('');
const rescheduleEndsAt = ref('');

const STATUS_BADGE = {
  scheduled: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  confirmed:
    'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  rescheduled:
    'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  completed: 'bg-n-alpha-1 text-n-slate-10',
  cancelled: 'bg-ruby-50 text-ruby-700 dark:bg-ruby-900/30 dark:text-ruby-300',
  no_show:
    'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
};

const VISIT_TYPE_ICON = {
  site_visit: 'i-lucide-map-pin',
  video_call: 'i-lucide-video',
  office_meeting: 'i-lucide-building-2',
};

const statusBadge = computed(() => STATUS_BADGE[props.visit?.status] || '');
const visitIcon = computed(
  () => VISIT_TYPE_ICON[props.visit?.visit_type] || 'i-lucide-calendar'
);
const leadName = computed(
  () => props.visit?.lead?.name || `Lead #${props.visit?.lead_id}`
);
const projectName = computed(() => props.visit?.project?.name || '—');
const agentName = computed(() => props.visit?.assigned_agent?.name || '—');
const isActive = computed(() =>
  ['scheduled', 'confirmed', 'rescheduled'].includes(props.visit?.status)
);

// M26.15.8 — scheduled_at_ist is pre-formatted in IST by the backend
// (Nexceo::TimeFormatter); previously this parsed the raw UTC scheduled_at
// with the *browser's* local offset instead of IST.
const dateTimeDisplay = computed(() => props.visit?.scheduled_at_ist || '—');

watch(
  () => props.isOpen,
  open => {
    if (!open) {
      showCancel.value = false;
      showReschedule.value = false;
      showMeetLinkInput.value = false;
      cancelReason.value = '';
      rescheduleAt.value = '';
      rescheduleEndsAt.value = '';
      meetLinkInput.value = '';
      actionError.value = '';
      retryMeetResult.value = null;
    }
  }
);

async function saveMeetLink() {
  if (!meetLinkInput.value.trim() || !props.visit) return;
  isSavingMeetLink.value = true;
  try {
    await siteVisitsAPI.updateMeetingLink(
      props.visit.id,
      meetLinkInput.value.trim()
    );
    await siteVisitsAPI.resendConfirmation(props.visit.id);
    showMeetLinkInput.value = false;
    meetLinkInput.value = '';
    useAlert('Meeting link saved and confirmation sent.');
    emit('updated');
  } catch {
    useAlert('Failed to save meeting link.');
  } finally {
    isSavingMeetLink.value = false;
  }
}

function toDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function startReschedule() {
  rescheduleAt.value = toDatetimeLocal(props.visit?.scheduled_at);
  rescheduleEndsAt.value = toDatetimeLocal(props.visit?.ends_at);
  showReschedule.value = true;
  showCancel.value = false;
}

async function act(action) {
  if (!props.visit) return;
  isActing.value = true;
  actionError.value = '';
  try {
    if (action === 'confirm') {
      await store.dispatch('nexceoSiteVisits/confirmSiteVisit', props.visit.id);
    } else if (action === 'complete') {
      await store.dispatch(
        'nexceoSiteVisits/completeSiteVisit',
        props.visit.id
      );
    } else if (action === 'cancel') {
      await store.dispatch('nexceoSiteVisits/cancelSiteVisit', {
        id: props.visit.id,
        reason: cancelReason.value,
      });
      showCancel.value = false;
    } else if (action === 'reschedule') {
      if (!rescheduleAt.value) return;
      await store.dispatch('nexceoSiteVisits/rescheduleSiteVisit', {
        id: props.visit.id,
        scheduledAt: new Date(rescheduleAt.value).toISOString(),
        endsAt: rescheduleEndsAt.value
          ? new Date(rescheduleEndsAt.value).toISOString()
          : null,
      });
      showReschedule.value = false;
    } else if (action === 'resend') {
      await siteVisitsAPI.resendConfirmation(props.visit.id);
    }
    emit('updated');
  } catch (err) {
    actionError.value =
      err?.response?.data?.error || t('NEXCEO.SITE_VISIT.ERROR');
  } finally {
    isActing.value = false;
  }
}

async function retryMeetLink() {
  if (!props.visit) return;
  isRetryingMeet.value = true;
  retryMeetResult.value = null;
  try {
    const { data } = await siteVisitsAPI.retryMeetLink(props.visit.id);
    retryMeetResult.value = data;
    emit('updated');
  } catch (err) {
    retryMeetResult.value = {
      success: false,
      error: err?.response?.data?.error || 'Failed to create Meet link.',
    };
  } finally {
    isRetryingMeet.value = false;
  }
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <Teleport to="body">
    <transition name="sv-drawer">
      <div v-if="isOpen && visit" class="fixed inset-0 z-50 flex justify-end">
        <!-- Overlay -->
        <div
          class="absolute inset-0 bg-n-slate-12/30 dark:bg-n-slate-1/50"
          @click="$emit('close')"
        />
        <div
          class="relative z-10 w-full max-w-md bg-white dark:bg-n-solid-2 h-full shadow-xl flex flex-col overflow-hidden"
        >
          <!-- Header -->
          <div
            class="flex items-center justify-between px-5 h-16 border-b border-n-weak dark:border-n-strong shrink-0"
          >
            <div class="flex items-center gap-2">
              <span class="size-5 text-woot-500" :class="[visitIcon]" />
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ t('NEXCEO.SITE_VISIT.DRAWER_TITLE') }}
              </h2>
            </div>
            <button
              class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-8 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
              @click="$emit('close')"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>

          <!-- Body -->
          <div
            class="flex-1 overflow-y-auto divide-y divide-n-weak dark:divide-n-strong"
          >
            <div class="px-5 py-4 flex flex-col gap-4">
              <!-- Status + type -->
              <div class="flex items-center gap-2">
                <span
                  class="px-3 py-1 rounded-full text-xs font-semibold"
                  :class="statusBadge"
                >
                  {{ t(`NEXCEO.CALENDAR.STATUS.${visit.status}`) }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ t(`NEXCEO.CALENDAR.VISIT_TYPE.${visit.visit_type}`) }}
                </span>
              </div>

              <!-- Info rows -->
              <div class="flex flex-col gap-4">
                <!-- Date & Time -->
                <div class="flex items-start gap-3">
                  <span
                    class="i-lucide-clock size-4 text-n-slate-9 mt-0.5 shrink-0"
                  />
                  <div>
                    <p class="text-xs text-n-slate-10">
                      {{ t('NEXCEO.SITE_VISIT.FIELDS.DATE_TIME') }}
                    </p>
                    <p class="text-sm text-n-slate-12">{{ dateTimeDisplay }}</p>
                  </div>
                </div>
                <!-- Lead -->
                <div class="flex items-start gap-3">
                  <span
                    class="i-lucide-user size-4 text-n-slate-9 mt-0.5 shrink-0"
                  />
                  <div>
                    <p class="text-xs text-n-slate-10">
                      {{ t('NEXCEO.SITE_VISIT.FIELDS.LEAD') }}
                    </p>
                    <p class="text-sm text-n-slate-12">{{ leadName }}</p>
                    <p
                      v-if="visit.lead?.phone_number"
                      class="text-xs text-n-slate-10"
                    >
                      {{ visit.lead.phone_number }}
                    </p>
                  </div>
                </div>
                <!-- Project -->
                <div class="flex items-start gap-3">
                  <span
                    class="i-lucide-building-2 size-4 text-n-slate-9 mt-0.5 shrink-0"
                  />
                  <div>
                    <p class="text-xs text-n-slate-10">
                      {{ t('NEXCEO.SITE_VISIT.FIELDS.PROJECT') }}
                    </p>
                    <p class="text-sm text-n-slate-12">{{ projectName }}</p>
                  </div>
                </div>
                <!-- Agent -->
                <div class="flex items-start gap-3">
                  <span
                    class="i-lucide-user-check size-4 text-n-slate-9 mt-0.5 shrink-0"
                  />
                  <div>
                    <p class="text-xs text-n-slate-10">
                      {{ t('NEXCEO.SITE_VISIT.FIELDS.AGENT') }}
                    </p>
                    <p class="text-sm text-n-slate-12">{{ agentName }}</p>
                  </div>
                </div>
                <!-- Location -->
                <div v-if="visit.location" class="flex items-start gap-3">
                  <span
                    class="i-lucide-map-pin size-4 text-n-slate-9 mt-0.5 shrink-0"
                  />
                  <div>
                    <p class="text-xs text-n-slate-10">
                      {{ t('NEXCEO.SITE_VISIT.FIELDS.LOCATION') }}
                    </p>
                    <p class="text-sm text-n-slate-12">{{ visit.location }}</p>
                  </div>
                </div>
                <!-- Google Meet / Meeting link -->
                <div
                  v-if="visit.meeting_url || visit.meeting_link"
                  class="flex items-start gap-3"
                >
                  <span
                    class="i-lucide-video size-4 text-n-slate-9 mt-0.5 shrink-0"
                  />
                  <div class="min-w-0">
                    <p class="text-xs text-n-slate-10 flex items-center gap-1">
                      {{
                        visit.meeting_url
                          ? 'Google Meet'
                          : t('NEXCEO.SITE_VISIT.FIELDS.MEETING_LINK')
                      }}
                      <span
                        v-if="visit.meeting_status === 'pending'"
                        class="text-amber-500 text-[10px] font-medium"
                      >
                        (pending)
                      </span>
                      <span
                        v-else-if="visit.meeting_status === 'failed'"
                        class="text-ruby-500 text-[10px] font-medium"
                      >
                        (failed)
                      </span>
                    </p>
                    <a
                      :href="visit.meeting_url || visit.meeting_link"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-sm text-woot-500 hover:underline truncate block max-w-[260px] font-medium"
                    >
                      Join Meeting
                    </a>
                    <p
                      class="text-[11px] text-n-slate-8 truncate max-w-[260px]"
                    >
                      {{ visit.meeting_url || visit.meeting_link }}
                    </p>
                  </div>
                </div>
                <!-- Calendar Sync Status -->
                <div
                  v-if="
                    visit.calendar_sync_status &&
                    visit.calendar_sync_status !== 'not_synced'
                  "
                  class="flex items-start gap-3"
                >
                  <span
                    class="i-lucide-calendar-check size-4 mt-0.5 shrink-0"
                    :class="
                      visit.calendar_sync_status === 'synced'
                        ? 'text-success-600'
                        : 'text-amber-500'
                    "
                  />
                  <div>
                    <p class="text-xs text-n-slate-10">Calendar Sync</p>
                    <p
                      class="text-sm capitalize"
                      :class="
                        visit.calendar_sync_status === 'synced'
                          ? 'text-success-700'
                          : 'text-amber-700'
                      "
                    >
                      {{ visit.calendar_sync_status }}
                      <span
                        v-if="
                          visit.calendar_synced_at &&
                          visit.calendar_sync_status === 'synced'
                        "
                        class="text-xs text-n-slate-8 ml-1 normal-case"
                      >
                        {{
                          new Date(visit.calendar_synced_at).toLocaleTimeString(
                            [],
                            { hour: '2-digit', minute: '2-digit' }
                          )
                        }}
                      </span>
                    </p>
                    <p
                      v-if="visit.calendar_sync_error"
                      class="text-xs text-ruby-600 mt-0.5"
                    >
                      {{ visit.calendar_sync_error }}
                    </p>
                  </div>
                </div>
                <!-- Notes -->
                <div v-if="visit.notes" class="flex items-start gap-3">
                  <span
                    class="i-lucide-file-text size-4 text-n-slate-9 mt-0.5 shrink-0"
                  />
                  <div>
                    <p class="text-xs text-n-slate-10">
                      {{ t('NEXCEO.SITE_VISIT.FIELDS.NOTES') }}
                    </p>
                    <p class="text-sm text-n-slate-12 whitespace-pre-line">
                      {{ visit.notes }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Enter meeting link (video_call without link) -->
            <div
              v-if="
                visit?.visit_type === 'video_call' &&
                !visit?.meeting_url &&
                !visit?.meeting_link
              "
              class="px-5 py-4 border-t border-n-weak"
            >
              <div class="flex items-center justify-between gap-2 mb-2">
                <p
                  class="text-xs font-medium text-amber-700 dark:text-amber-400 flex items-center gap-1.5"
                >
                  <span class="i-lucide-alert-triangle size-3.5" />
                  No Google Meet link — confirmation not sent yet
                </p>
                <div class="flex items-center gap-2">
                  <!-- Auto-create via Google Calendar API -->
                  <button
                    :disabled="isRetryingMeet"
                    class="text-xs text-woot-500 hover:underline font-medium flex items-center gap-1 disabled:opacity-50"
                    @click="retryMeetLink"
                  >
                    <span
                      :class="
                        isRetryingMeet
                          ? 'i-lucide-loader-2 size-3 animate-spin'
                          : 'i-lucide-refresh-cw size-3'
                      "
                    />
                    {{ isRetryingMeet ? 'Creating…' : 'Auto-Create Meet' }}
                  </button>
                  <!-- Manual fallback -->
                  <button
                    v-if="!showMeetLinkInput"
                    class="text-xs text-n-slate-9 hover:underline font-medium flex items-center gap-1"
                    @click="showMeetLinkInput = true"
                  >
                    <span class="i-lucide-link size-3" /> Manual Link
                  </button>
                </div>
              </div>
              <!-- Retry result -->
              <div
                v-if="retryMeetResult"
                class="mb-2 text-xs px-3 py-2 rounded-lg"
                :class="
                  retryMeetResult.success
                    ? 'bg-success-50 text-success-700'
                    : 'bg-ruby-50 text-ruby-700'
                "
              >
                <span
                  :class="
                    retryMeetResult.success
                      ? 'i-lucide-check-circle size-3.5 inline mr-1'
                      : 'i-lucide-x-circle size-3.5 inline mr-1'
                  "
                />
                {{
                  retryMeetResult.success
                    ? retryMeetResult.message ||
                      'Meet link created and confirmation sent.'
                    : retryMeetResult.error
                }}
                <a
                  v-if="retryMeetResult.meeting_url"
                  :href="retryMeetResult.meeting_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="ml-1 text-woot-600 underline"
                  >Join Meet</a
                >
              </div>
              <div v-if="showMeetLinkInput" class="flex flex-col gap-2">
                <p class="text-xs text-n-slate-8">
                  Create a Meet at
                  <a
                    href="https://meet.google.com/new"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-woot-500 underline"
                    >meet.google.com/new</a
                  >, then paste below:
                </p>
                <input
                  v-model="meetLinkInput"
                  type="url"
                  placeholder="https://meet.google.com/xxx-yyyy-zzz"
                  class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-white dark:bg-n-solid-3 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
                />
                <div class="flex gap-2">
                  <button
                    class="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-woot-500 text-white text-xs font-medium rounded-lg hover:bg-woot-600 disabled:opacity-50 transition-colors"
                    :disabled="!meetLinkInput.trim() || isSavingMeetLink"
                    @click="saveMeetLink"
                  >
                    <span
                      v-if="isSavingMeetLink"
                      class="i-lucide-loader-2 size-3.5 animate-spin"
                    />
                    <span v-else class="i-lucide-send size-3.5" />
                    Save & Send Confirmation
                  </button>
                  <button
                    class="px-3 py-1.5 border border-n-weak rounded-lg text-xs text-n-slate-9 hover:bg-n-alpha-1 transition-colors"
                    @click="showMeetLinkInput = false"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>

            <!-- Reminder status -->
            <div class="px-5 py-4">
              <h3
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
              >
                Reminder Status
              </h3>
              <div class="grid grid-cols-2 gap-2">
                <div
                  class="flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg"
                  :class="
                    visit.customer_reminder_24h_sent_at
                      ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                      : 'bg-n-alpha-1 text-n-slate-9'
                  "
                >
                  <span
                    class="size-3 shrink-0"
                    :class="
                      visit.customer_reminder_24h_sent_at
                        ? 'i-lucide-check-circle'
                        : 'i-lucide-clock'
                    "
                  />
                  Customer 24h
                </div>
                <div
                  class="flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg"
                  :class="
                    visit.customer_reminder_2h_sent_at
                      ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                      : 'bg-n-alpha-1 text-n-slate-9'
                  "
                >
                  <span
                    class="size-3 shrink-0"
                    :class="
                      visit.customer_reminder_2h_sent_at
                        ? 'i-lucide-check-circle'
                        : 'i-lucide-clock'
                    "
                  />
                  Customer 2h
                </div>
                <div
                  class="flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg"
                  :class="
                    visit.agent_reminder_24h_sent_at
                      ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                      : 'bg-n-alpha-1 text-n-slate-9'
                  "
                >
                  <span
                    class="size-3 shrink-0"
                    :class="
                      visit.agent_reminder_24h_sent_at
                        ? 'i-lucide-check-circle'
                        : 'i-lucide-clock'
                    "
                  />
                  Agent 24h
                </div>
                <div
                  class="flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg"
                  :class="
                    visit.agent_reminder_2h_sent_at
                      ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                      : 'bg-n-alpha-1 text-n-slate-9'
                  "
                >
                  <span
                    class="size-3 shrink-0"
                    :class="
                      visit.agent_reminder_2h_sent_at
                        ? 'i-lucide-check-circle'
                        : 'i-lucide-clock'
                    "
                  />
                  Agent 2h
                </div>
              </div>
              <p
                v-if="visit.reminder_error"
                class="mt-2 text-xs text-ruby-600 dark:text-ruby-400"
              >
                Error: {{ visit.reminder_error }}
              </p>
            </div>

            <!-- Error -->
            <div
              v-if="actionError"
              class="px-5 py-3 text-sm text-ruby-700 bg-ruby-50 dark:bg-ruby-500/10"
            >
              {{ actionError }}
            </div>

            <!-- Reschedule panel -->
            <div v-if="showReschedule" class="px-5 py-4 flex flex-col gap-3">
              <p class="text-sm font-medium text-n-slate-12">
                {{ t('NEXCEO.SITE_VISIT.ACTIONS.RESCHEDULE') }}
              </p>
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-11">
                  {{ t('NEXCEO.SITE_VISIT.FIELDS.DATE_TIME') }} *
                </label>
                <input
                  v-model="rescheduleAt"
                  type="datetime-local"
                  class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-white dark:bg-n-solid-3 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
                />
              </div>
              <div class="flex gap-2">
                <button
                  :disabled="!rescheduleAt || isActing"
                  class="flex-1 py-2 rounded-lg text-sm font-medium bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50 transition-colors"
                  @click="act('reschedule')"
                >
                  <span
                    v-if="isActing"
                    class="i-lucide-loader-2 animate-spin mr-1"
                  />
                  {{ t('NEXCEO.SITE_VISIT.ACTIONS.SAVE_RESCHEDULE') }}
                </button>
                <button
                  class="px-3 py-2 rounded-lg text-sm border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
                  @click="showReschedule = false"
                >
                  {{ t('NEXCEO.SITE_VISIT.ACTIONS.CLOSE') }}
                </button>
              </div>
            </div>

            <!-- Cancel panel -->
            <div v-if="showCancel" class="px-5 py-4 flex flex-col gap-3">
              <p class="text-sm font-medium text-ruby-700">
                {{ t('NEXCEO.SITE_VISIT.CONFIRM_CANCEL') }}
              </p>
              <input
                v-model="cancelReason"
                type="text"
                :placeholder="t('NEXCEO.SITE_VISIT.FIELDS.CANCEL_REASON')"
                class="w-full border border-ruby-200 dark:border-ruby-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-n-solid-3 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-ruby-400"
              />
              <div class="flex gap-2">
                <button
                  :disabled="isActing"
                  class="flex-1 py-2 rounded-lg text-sm font-medium bg-ruby-600 text-white hover:bg-ruby-700 disabled:opacity-50 transition-colors"
                  @click="act('cancel')"
                >
                  <span
                    v-if="isActing"
                    class="i-lucide-loader-2 animate-spin mr-1"
                  />
                  {{ t('NEXCEO.SITE_VISIT.ACTIONS.CANCEL') }}
                </button>
                <button
                  class="px-3 py-2 rounded-lg text-sm border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
                  @click="showCancel = false"
                >
                  {{ t('NEXCEO.SITE_VISIT.ACTIONS.CLOSE') }}
                </button>
              </div>
            </div>
          </div>

          <!-- Action footer (active visits only) -->
          <div
            v-if="isActive && !showReschedule && !showCancel"
            class="border-t border-n-weak dark:border-n-strong px-5 py-4 flex flex-col gap-2 shrink-0"
          >
            <!-- Join Meet — primary CTA for video calls with a link -->
            <a
              v-if="
                visit.visit_type === 'video_call' &&
                (visit.meeting_url || visit.meeting_link)
              "
              :href="visit.meeting_url || visit.meeting_link"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center justify-center gap-2 w-full py-2 rounded-lg text-sm font-medium bg-woot-500 text-white hover:bg-woot-600 transition-colors"
            >
              <span class="i-lucide-video size-4" />
              Join Google Meet
            </a>
            <div class="flex gap-2">
              <button
                v-if="['scheduled', 'rescheduled'].includes(visit.status)"
                :disabled="isActing"
                class="flex-1 py-2 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                @click="act('confirm')"
              >
                <span
                  v-if="isActing"
                  class="i-lucide-loader-2 animate-spin mr-1"
                />
                {{ t('NEXCEO.SITE_VISIT.ACTIONS.CONFIRM') }}
              </button>
              <button
                :disabled="isActing"
                class="flex-1 py-2 rounded-lg text-sm font-medium border border-woot-500 text-woot-500 hover:bg-woot-50 disabled:opacity-50 transition-colors"
                @click="act('complete')"
              >
                {{ t('NEXCEO.SITE_VISIT.ACTIONS.COMPLETE') }}
              </button>
            </div>
            <div class="flex gap-2">
              <button
                :disabled="isActing"
                class="flex-1 py-2 rounded-lg text-sm font-medium border border-n-weak dark:border-n-strong text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-50 transition-colors"
                @click="startReschedule"
              >
                {{ t('NEXCEO.SITE_VISIT.ACTIONS.RESCHEDULE') }}
              </button>
              <button
                :disabled="isActing"
                class="flex-1 py-2 rounded-lg text-sm font-medium text-ruby-600 border border-ruby-200 hover:bg-ruby-50 dark:hover:bg-ruby-500/10 disabled:opacity-50 transition-colors"
                @click="
                  showCancel = true;
                  showReschedule = false;
                "
              >
                {{ t('NEXCEO.SITE_VISIT.ACTIONS.CANCEL') }}
              </button>
            </div>
            <!-- Resend confirmation -->
            <button
              :disabled="isActing"
              class="w-full py-2 rounded-lg text-xs font-medium border border-n-weak text-n-slate-10 hover:bg-n-alpha-1 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
              @click="act('resend')"
            >
              <span class="i-lucide-send size-3.5" />
              Resend Confirmation
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.sv-drawer-enter-active {
  transition: opacity 0.2s ease;
}
.sv-drawer-leave-active {
  transition: opacity 0.15s ease;
}
.sv-drawer-enter-active .relative {
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}
.sv-drawer-leave-active .relative {
  transition: transform 0.2s ease;
}
.sv-drawer-enter-from,
.sv-drawer-leave-to {
  opacity: 0;
}
.sv-drawer-enter-from .relative {
  transform: translateX(100%);
}
.sv-drawer-leave-to .relative {
  transform: translateX(100%);
}
</style>
